// FilterableTable.js
import { Box, Paper, Table as MuiTable, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Typography, Skeleton } from "@mui/material";

import PropTypes from "prop-types";
import { visuallyHidden } from "@mui/utils";
import _ from "lodash";

const TableHeadCustom = ({ columns, order, orderBy, onSort }) => {
  const createSortHandler = (property) => (event) => {
    if (property === "id") return;
    onSort(event, property);
  };

  return (
    <TableHead>
      <TableRow
        sx={{
          background: "#F0F0F0",
          borderTop: "1px solid #D4D4D4",
          borderBottom: "1px solid #D4D4D4",
        }}>
        {columns
          .filter((col) => col?.condition === undefined || col.condition)
          .map((col) => (
            <TableCell key={col.id} align={col?.props?.align || "center"} sortDirection={orderBy === col.id ? order : false}>
              <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : "asc"} onClick={createSortHandler(col.id)}>
                {col.label}
                {orderBy === col.id && (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                )}
              </TableSortLabel>
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
};

TableHeadCustom.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      condition: PropTypes.bool,
    }),
  ).isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
};

const LoadingRows = ({ columns }) => (
  <>
    {Array.from({ length: 5 }).map(() => {
      const rowKey = crypto.randomUUID();
      return (
        <TableRow key={rowKey}>
          {columns.map((col) => (
            <TableCell key={`loading-cell-${col.id}`}>
              <Skeleton animation="wave" sx={{ my: 1 }} />
            </TableCell>
          ))}
        </TableRow>
      );
    })}
  </>
);

LoadingRows.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

const UserTable = ({ columns, data, isLoading, page, rowsPerPage, order, orderBy, onSort, onPageChange, onRowsPerPageChange, shortenContent, noResultsContent, totalData }) => (
  <Box sx={{ width: "95%", margin: "auto" }}>
    <Paper sx={{ width: "100%", mb: 2 }}>
      <TableContainer>
        <MuiTable>
          <TableHeadCustom columns={columns} order={order} orderBy={orderBy} onSort={onSort} />

          <TableBody>
            {isLoading ? (
              <LoadingRows columns={columns} />
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ height: 180 }}>
                  <Typography>{noResultsContent}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow key={row.id || index} hover>
                  {columns
                    .filter((c) => c?.condition === undefined || c.condition)
                    .map((col) => (
                      <TableCell key={col.id} align={col?.props?.align || "center"}>{col.value ? (shortenContent ? _.truncate(col.value(row), { length: 60 }) : col.value(row)) : row[col.id]}</TableCell>
                    ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>

      {data.length > 0 && (
        <TablePagination
          component="div"
          count={totalData}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}
    </Paper>
  </Box>
);

UserTable.defaultProps = {
  isLoading: false,
  shortenContent: false,
  noResultsContent: "There are no results.",
  totalData: 0,
};

UserTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      value: PropTypes.func,
      condition: PropTypes.bool,
    }),
  ).isRequired,

  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ).isRequired,

  isLoading: PropTypes.bool,

  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,

  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,

  onSort: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,

  shortenContent: PropTypes.bool,
  noResultsContent: PropTypes.string,
  totalData: PropTypes.number,
};

export default UserTable;
